import { useMemo } from 'react';
import type { OptionType } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { validCountryAlpha2Code } from '@shared/hooks/useGeoLocale/countries';
import { COUNTRY_LIST } from './constants';

type LocalizedCountryOptions = Readonly<{
  /** COUNTRY_LIST with labels localized through the `countries` namespace, sorted by localized label. */
  countryOptions: Array<OptionType>;
  /** Localized display name for an alpha-2 country code; undefined when the code has no translation entry. */
  localizedCountryName: (countryCode: string | undefined) => string | undefined;
}>;

/**
 * Localized version of {@link COUNTRY_LIST} for country dropdowns on translated pages.
 *
 * Only the display labels are localized — option values stay the canonical uppercase
 * alpha-2 codes, and countries without an entry in the `countries` translation
 * namespace keep their English label. Callers persisting a country *name* should keep
 * storing the canonical English label from COUNTRY_LIST (looked up by code), not the
 * localized one.
 */
export const useLocalizedCountryOptions = (): LocalizedCountryOptions => {
  const { t } = useTranslation('countries');

  return useMemo(() => {
    const localizedCountryName = (countryCode: string | undefined): string | undefined => {
      const alpha2 = countryCode ? validCountryAlpha2Code(countryCode) : undefined;
      return alpha2 ? t(alpha2)() : undefined;
    };

    const countryOptions = COUNTRY_LIST.map(({ label, value }) => ({
      label: localizedCountryName(value) ?? label,
      value,
    })).sort((a, b) => a.label.localeCompare(b.label));

    return { countryOptions, localizedCountryName };
  }, [t]);
};
